/**
 * Keyboard shortcuts for the drill.
 *
 * A drill is the same three keystrokes a few hundred times, and reaching for a mouse
 * between every one of them is the difference between a tool somebody uses for an hour
 * and one they use for five minutes. The letters are the first letter of the action
 * wherever that is free, because a shortcut you have to look up is a shortcut nobody
 * uses — and the legend is on screen anyway, because a shortcut nobody is told about
 * is worse still.
 *
 * Only actions that EXIST get a key. The prototype this screen is modelled on showed
 * F / C / B / R / N, and B and R would be dead here: shove-or-fold has no bet and no
 * raise, and a legend advertising keys that do nothing is the same lie as a button
 * that does nothing.
 */
const LETTER: Record<string, string> = {
  fold: "F",
  call: "C",
  check: "K",     // C is taken by call, and "check" is K on every poker client there is
  shove: "A",     // "all in" — S reads as "stand", and the blackjack room next door uses it
  bet: "B",
  raise: "R",
};

/**
 * The key for an action, or null when it has none.
 *
 * Null rather than a made-up letter: the caller renders the legend from this, so an
 * unmapped action simply has no key rather than one that collides with another.
 */
export function shortcutFor(id: string): string | null {
  return LETTER[id] ?? null;
}

/**
 * Which action a keystroke means, given the actions actually on screen.
 *
 * Digits are a universal fallback — 1 is the first button, 2 the second — so every
 * action is reachable even if a future node introduces one with no letter. Returns
 * null for anything else, and the caller must not preventDefault on a null.
 */
export function actionForKey(key: string, actions: readonly { id: string }[]): string | null {
  const k = key.toUpperCase();
  if (/^[1-9]$/.test(k)) return actions[Number(k) - 1]?.id ?? null;
  for (const a of actions) if (shortcutFor(a.id) === k) return a.id;
  return null;
}
